import React, { createContext, FC, ReactNode } from "react";
import {
  ParsedTestCard,
  SecondaryActionCardType,
  SecondaryCard,
  TestCard,
  TestCardStatus,
  TestType,
} from "@app/provider/testData/types";
import { useIntl } from "react-intl";
import {
  getSecondaryCardDescription,
  getPrimaryAction,
  getDescription,
  getSecondaryCardTitle,
} from "./utils";
import { getSecondaryCardDescriptionNote } from "@app/provider/testData/utils/getSecondaryCardDescriptionNote";
import { isConnectPortal, isOrganHealthPortal } from "@app/utils";
import { TestCardBuilder } from "@app/provider/testData/testCard/TestCardBuilder";

export interface TestCardUtilsController {
  getTestCardData: (testCard: TestCard) => ParsedTestCard;
  getSecondaryActionCardData: (secondaryCard: SecondaryCard) => SecondaryCard;
}

export const Context = createContext<TestCardUtilsController>({
  getTestCardData: () => ({
    uid: "uid",
    orderUidPublic: "orderUidPublic",
    testType: TestType.PROSPERA,
    createdAt: new Date(),
    status: TestCardStatus.TEST_ORDERED,
    resultReleasedAt: null,
    clinic: { limsId: 1 },
  }),
  getSecondaryActionCardData: () => ({
    type: SecondaryActionCardType.HORIZON,
  }),
});

const TestCardUtilsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const intl = useIntl();

  const getTestCardData = (testCard: TestCard): ParsedTestCard => {
    let testCardData: ParsedTestCard | undefined;

    // Rules-based cards carry i18nKeys and skip the legacy client builder.
    if (
      !testCard.i18nKeys &&
      (isConnectPortal(testCard.testType) ||
        isOrganHealthPortal(testCard.testType) ||
        testCard.testType === TestType.LATITUDE)
    ) {
      const testCardBuilder = new TestCardBuilder();
      testCardData = testCardBuilder.buildTestCard(testCard, intl);
    }

    if (!testCardData) {
      testCardData = {
        uid: testCard.uid,
        orderUidPublic: testCard.orderUidPublic,
        testType: testCard.testType,
        createdAt: testCard.createdAt,
        accessedResultAt: testCard.accessedResultAt,
        status: testCard.status,
        nextStatus: testCard.nextStatus,
        description: getDescription(intl, testCard),
        primaryAction: getPrimaryAction(testCard),
        organType: testCard.organType,
        sampleType: testCard.sampleType,
        sampleReceivedDate: testCard.sampleReceivedDate,
        trackingNumber: testCard.trackingNumber,
        reportGender: testCard.reportGender,
        caPnsSupplemental: testCard.caPnsSupplemental,
        resultCode: testCard.resultCode,
        fetalSex: testCard.fetalSex,
        resultReleasedAt: testCard.resultReleasedAt,
        latestResultDocumentUid: testCard.latestResultDocumentUid,
        cancelledAt: testCard.cancelledAt,
        isProReach: testCard.isProReach,
        clinic: testCard.clinic,
        isComboOrder: testCard.isComboOrder,
        managedBy: testCard.managedBy,
        drawCoordinator: testCard.drawCoordinator,
        isNevaEnabled: testCard.isNevaEnabled,
        isOrderInSignedOrdersTable: testCard.isOrderInSignedOrdersTable,
        simpleOrderWorkflowState: testCard.simpleOrderWorkflowState,
        isSignateraSubsequent: testCard.isSignateraSubsequent,
        isEmpowerStatPanelIncluded: testCard.isEmpowerStatPanelIncluded,
        isLatestReleasedResultStat: testCard.isLatestReleasedResultStat,
        isStatResultAlreadyViewed: testCard.isStatResultAlreadyViewed,
        isTfMrdActive: testCard.isTfMrdActive,
        tfMrdOrderingOption: testCard.tfMrdOrderingOption,
        clinicalInfo: testCard.clinicalInfo,
        isPatientResultReleaseDelayed: testCard.isPatientResultReleaseDelayed,
        patientResultAvailableAt: testCard.patientResultAvailableAt,
        // Pass through i18nKeys for rules-based cards
        i18nKeys: testCard.i18nKeys,
      };
    }
    return testCardData;
  };

  const getSecondaryActionCardData = (secondaryCard: SecondaryCard) => ({
    type: secondaryCard.type,
    title: getSecondaryCardTitle(intl, secondaryCard),
    description: getSecondaryCardDescription(intl, secondaryCard),
    note: getSecondaryCardDescriptionNote(intl, secondaryCard),
    limsClinicId: secondaryCard.limsClinicId,
  });

  return (
    <Context.Provider
      value={{
        getTestCardData,
        getSecondaryActionCardData,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default TestCardUtilsProvider;
